import { useEffect, useState } from 'react';
import {
  Table,
  Loader,
  Center,
  Text,
  Button,
  Badge,
  Modal,
  ActionIcon,
  Anchor,
  UnstyledButton,
} from '@mantine/core';
import {
  IconX,
  IconEye,
  IconUser,
  IconPhone,
  IconMail,
  IconWorld,
  IconBriefcase,
  IconCoins,
  IconCalendar,
  IconFileText,
  IconMessage,
  IconPaperclip,
  IconDownload,
} from '@tabler/icons-react';
import { Page } from '@/components';
import { useTranslation } from 'react-i18next';
import { useGetAllLeadMutation, useGetLeadFileMutation } from '@/services/api';
import { getMutationResult } from '@/helpers/query';
import classes from './lead.module.css';

const SOURCE_LABELS = {
  'landing-page': 'דף נחיתה',
  site: 'אתר',
};

const SOURCE_BADGE_COLORS = {
  'landing-page': 'grape',
  site: 'blue',
};

const NO_INFO = 'לא קיים מידע';

const getFileName = (filePath) => filePath.split('/').pop().replace(/^[0-9a-fA-F-]{36}-/, '');

// Leads have no dedicated timestamp on the client model; prefer the backend's
// createdAt when present, otherwise derive it from the Mongo _id.
const getLeadDate = (lead) => {
  if (lead?.createdAt) return new Date(lead.createdAt);
  if (typeof lead?._id === 'string' && lead._id.length >= 8) {
    return new Date(parseInt(lead._id.substring(0, 8), 16) * 1000);
  }
  return null;
};

const formatDateTime = (date) => {
  if (!date) return '-';

  return date.toLocaleString('he-IL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const SERVICE_TYPE_LABELS = {
  new: 'משכנתא חדשה',
  refinance: 'מחזור משכנתא',
};

const GUIDANCE_TYPE_LABELS = {
  call: 'שיחת ייעוץ',
  focused: 'ליווי ממוקד',
  full: 'ליווי אישי מלא',
};

const Leads = () => {
  const [data, setData] = useState([]);
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const { t } = useTranslation();
  const [apiGetAllLeads] = useGetAllLeadMutation();
  const [apiGetLeadFile] = useGetLeadFileMutation();

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const { data: result } = await getMutationResult({}, apiGetAllLeads);
        const { success, data: leadsData } = result;

        if (success) {
          setData(leadsData);
        } else {
          setIsError(true);
        }
      } catch (error) {
        setIsError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadFile = async (filePath) => {
    setDownloading(true);

    try {
      const { data: d } = await getMutationResult({ filePath }, apiGetLeadFile);

      if (!d.success) return;

      const byteArray = new Uint8Array(Object.values(d.data.data));
      const blob = new Blob([byteArray]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = getFileName(filePath);
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      /* empty */
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return <Center><Loader /></Center>;
  }

  if (isError) {
    return <Center><Text c="red">{t('pages.lead.noData') }</Text></Center>;
  }

  if (!data || data.length === 0) {
    return <Center><Text>{t('pages.lead.noData') }</Text></Center>;
  }

  const detailFields = selectedLead
    ? [
      { icon: IconUser, label: 'שם', value: selectedLead.fullName },
      { icon: IconPhone, label: 'טלפון', value: selectedLead.phone },
      {
        icon: IconMail, label: 'אימייל', value: selectedLead.email, href: `mailto:${selectedLead.email}`,
      },
      { icon: IconWorld, label: 'מקור', value: SOURCE_LABELS[selectedLead.source] },
      { icon: IconBriefcase, label: 'סוג שירות', value: SERVICE_TYPE_LABELS[selectedLead.serviceType] },
      { icon: IconUser, label: 'סוג ליווי', value: GUIDANCE_TYPE_LABELS[selectedLead.guidanceType] },
      { icon: IconCoins, label: 'סכום הלוואה', value: selectedLead.offerId?.loanAmount },
      { icon: IconCalendar, label: 'תקופה', value: selectedLead.offerId?.period },
      { icon: IconFileText, label: 'סוג הלוואה', value: selectedLead.offerId?.loan?.hebrew },
    ]
    : [];

  const headers = [
    t('pages.lead.headers.date'),
    t('pages.lead.headers.name'),
    t('pages.lead.headers.email'),
    t('pages.lead.headers.phone'),
    t('pages.lead.headers.source'),
    'סוכן',
    t('pages.lead.headers.loanAmount'),
    t('pages.lead.headers.loanType'),
    t('pages.lead.headers.actions'),
  ];

  const sortedData = [...data].sort(
    (a, b) => (getLeadDate(b)?.getTime() || 0) - (getLeadDate(a)?.getTime() || 0),
  );

  return (
    <Page hideHelp hideNews title={t('pages.lead.title')}>
      <div className={classes.tableContainer}>
        <Table striped withColumnBorders classNames={{ tr: `${classes.changeColor}`, th: classes.changeColor, td: classes.changeColor }}>
          <Table.Thead>
            <Table.Tr className={classes.headerRow}>
              {headers.map((header, index) => (
                <Table.Th key={index}>{header}</Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {sortedData.map((lead) => (
              <Table.Tr key={lead._id}>
                <Table.Td>{formatDateTime(getLeadDate(lead))}</Table.Td>
                <Table.Td>{lead.fullName}</Table.Td>
                <Table.Td>{lead.email}</Table.Td>
                <Table.Td>{lead.phone}</Table.Td>
                <Table.Td>
                  <Badge color={SOURCE_BADGE_COLORS[lead.source] || 'gray'} variant="light">
                    {SOURCE_LABELS[lead.source] || '-'}
                  </Badge>
                </Table.Td>
                <Table.Td>{lead.agentId?.name || '-'}</Table.Td>
                <Table.Td>{lead.offerId?.loanAmount || '-'}</Table.Td>
                <Table.Td>{lead.offerId?.period || '-'}</Table.Td>
                <Table.Td>
                  <UnstyledButton
                    className={classes.viewButton}
                    onClick={() => setSelectedLead(lead)}
                  >
                    <span>{t('pages.lead.moreInformation')}</span>
                    <IconEye size={18} stroke={1.5} />
                  </UnstyledButton>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>

        {selectedLead && (
          <Modal
            opened
            onClose={() => setSelectedLead(null)}
            withCloseButton={false}
            centered
            padding={0}
            radius={20}
            size="lg"
          >
            <div className={classes.detail}>
              <div className={classes.detailHeader}>
                <div className={classes.detailTitle}>
                  <span className={classes.detailTitleIcon}>
                    <IconUser size={22} />
                  </span>
                  <Text fw={700} fz="xl">פרטי ליד</Text>
                </div>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="lg"
                  radius="md"
                  aria-label="סגור"
                  onClick={() => setSelectedLead(null)}
                >
                  <IconX size={22} />
                </ActionIcon>
              </div>

              <div className={classes.detailGrid}>
                {detailFields.map((field) => (
                  <div key={field.label} className={classes.detailField}>
                    <div className={classes.detailFieldLabel}>
                      <field.icon size={18} stroke={1.5} />
                      <span>{field.label}:</span>
                    </div>
                    {field.href && field.value ? (
                      <Anchor href={field.href} className={classes.detailFieldValue} c="blue.4">
                        {field.value}
                      </Anchor>
                    ) : (
                      <span className={classes.detailFieldValue}>
                        {field.value || NO_INFO}
                      </span>
                    )}
                  </div>
                ))}

                {detailFields.length % 2 === 1 && (
                  <div className={classes.detailField} aria-hidden="true" />
                )}

                {selectedLead.message && (
                  <div className={`${classes.detailField} ${classes.detailFieldFull}`}>
                    <div className={classes.detailFieldLabel}>
                      <IconMessage size={18} stroke={1.5} />
                      <span>הודעה:</span>
                    </div>
                    <span className={classes.detailFieldValue} style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedLead.message}
                    </span>
                  </div>
                )}
              </div>

              {selectedLead.filePath && (
                <div className={classes.detailFile}>
                  <div className={classes.detailFileInfo}>
                    <div className={classes.detailFileTitle}>
                      <IconPaperclip size={18} stroke={1.5} />
                      <span>מסמך מצורף</span>
                    </div>
                    <span className={classes.detailFileName}>
                      {getFileName(selectedLead.filePath)}
                    </span>
                  </div>
                  <Button
                    color="blue"
                    loading={downloading}
                    leftSection={<IconDownload size={18} />}
                    onClick={() => handleDownloadFile(selectedLead.filePath)}
                  >
                    {t('pages.lead.downloadFile')}
                  </Button>
                </div>
              )}
            </div>
          </Modal>
        )}
      </div>
    </Page>
  );
};

export default Leads;
